// COA Category configuration - shared across all COA display/edit components
// Order: Cannabinoids first, then terpenes, then physical properties, then the rest

// Client fields that should NEVER be displayed (privacy protection)
export const CLIENT_INFO_FIELDS = [
  'client',
  'client_name',
  'client_address',
  'client_license',
  'client_id',
  'client_email',
  'client_phone',
  'submitter',
  'submitter_name',
  'submitter_address',
  'submitted_by',
  'customer',
  'customer_name',
  'customer_address',
  'company',
  'company_name',
  'company_address',
  'licensee',
  'licensee_name',
  'license_holder',
  'contact',
  'contact_name',
  'contact_email',
  'contact_phone',
] as const;

// Essential lab info fields to display (in priority order)
export const LAB_INFO_FIELDS = [
  'lab_name',
  'report_date',
  'sample_date',
  'received_date',
  'lab_license',
  'certifications',
  'accreditations',
  'report_id',
  'certificate_number',
] as const;

// Human-readable labels for lab fields
export const LAB_INFO_LABELS: Record<string, string> = {
  lab_name: 'Laboratory',
  report_date: 'Report Date',
  sample_date: 'Sample Date',
  received_date: 'Received Date',
  lab_license: 'Lab License',
  certifications: 'Certifications',
  accreditations: 'Accreditations',
  report_id: 'Report ID',
  certificate_number: 'Certificate Number',
};

// Priority order for displaying lab info fields
export const LAB_INFO_PRIORITY = [
  'lab_name',
  'report_date',
  'sample_date',
  'received_date',
  'lab_license',
  'certifications',
  'accreditations',
  'report_id',
  'certificate_number',
] as const;

export const COA_CATEGORY_ORDER = [
  'sample_info',
  'cannabinoids',
  'terpenes',
  'physical',
  'moisture',
  'heavy_metals',
  'pesticides',
  'microbials',
  'mycotoxins',
  'solvents',
  'other',
] as const;

export const COA_CATEGORY_LABELS: Record<string, string> = {
  sample_info: 'Sample Information',
  cannabinoids: 'Cannabinoids',
  terpenes: 'Terpene Profile',
  physical: 'Physical Properties',
  moisture: 'Moisture Content',
  heavy_metals: 'Heavy Metals',
  pesticides: 'Pesticides',
  microbials: 'Microbial Testing',
  mycotoxins: 'Mycotoxins',
  solvents: 'Residual Solvents',
  other: 'Other Results',
};

export const formatCOAKey = (key: string): string => {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const formatCOAValue = (value: string | number): string => {
  if (typeof value === 'number') {
    return value.toLocaleString(undefined, { maximumFractionDigits: 3 });
  }
  if (value === 'nd' || value === 'ND') {
    return 'ND';
  }
  if (value === 'pass' || value === 'PASS') {
    return 'Pass';
  }
  if (value === 'fail' || value === 'FAIL') {
    return 'Fail';
  }
  return String(value);
};

// Helper to get ordered categories from COA data (excludes lab_info)
export const getOrderedCategories = (coaData: Record<string, unknown>): string[] => {
  const orderedCategories = COA_CATEGORY_ORDER.filter(
    category => coaData[category] && typeof coaData[category] === 'object' && Object.keys(coaData[category] as object).length > 0
  );
  
  // Add any categories not in the predefined order (excluding lab_info)
  const additionalCategories = Object.keys(coaData).filter(
    category => 
      !COA_CATEGORY_ORDER.includes(category as typeof COA_CATEGORY_ORDER[number]) &&
      category !== 'lab_info'
  );
  
  return [...orderedCategories, ...additionalCategories];
};

// Merge coa_data and coa_data_additional_report into a single object
export const mergeCOAData = (metadata: Record<string, unknown> | null | undefined): Record<string, Record<string, string | number>> | undefined => {
  if (!metadata) return undefined;
  
  const primary = metadata.coa_data as Record<string, Record<string, string | number>> | undefined;
  const additional = metadata.coa_data_additional_report as Record<string, Record<string, string | number>> | undefined;
  
  if (!primary && !additional) return undefined;
  if (!additional) return primary;
  if (!primary) return additional;
  
  // Merge: primary takes precedence for lab_info, but add categories from additional
  const merged: Record<string, Record<string, string | number>> = { ...primary };
  
  Object.entries(additional).forEach(([key, value]) => {
    // Skip lab_info from additional (use primary's lab_info)
    if (key === 'lab_info') return;
    // Skip non-object values
    if (!value || typeof value !== 'object') return;
    // Add category if it doesn't exist in primary
    if (!merged[key]) {
      merged[key] = value;
    }
  });
  
  return merged;
};

// COA compliance field to badge label mapping
export const COA_COMPLIANCE_LABELS: Record<string, string> = {
  no_pesticides: 'No Pesticides Detected',
  no_heavy_metals: 'No Heavy Metals Detected',
  microbials_pass: 'Microbial Testing Passed',
  no_mycotoxins: 'No Mycotoxins Detected',
  no_solvents: 'No Residual Solvents Detected',
  moisture_pass: 'Moisture Testing Passed',
  foreign_matter_pass: 'Foreign Matter Testing Passed',
};

// Interface for COA compliance object
export interface COACompliance {
  [key: string]: boolean | undefined;
  no_pesticides?: boolean;
  no_heavy_metals?: boolean;
  microbials_pass?: boolean;
  no_mycotoxins?: boolean;
  no_solvents?: boolean;
  moisture_pass?: boolean;
  foreign_matter_pass?: boolean;
}

// Get COA compliance object from metadata (and optional raw listing for top-level RPC fields)
// Priority: listing.coa_compliance → metadata.coa_compliance → metadata.compliance → metadata.coa_data.coa_compliance
export const getCOACompliance = (
  metadata: Record<string, unknown> | null | undefined,
  listing?: Record<string, unknown> | null,
): COACompliance | null => {
  // 1. Top-level field from search_listings_scored RPC
  if (listing?.coa_compliance && typeof listing.coa_compliance === 'object') {
    return listing.coa_compliance as COACompliance;
  }
  
  if (!metadata) return null;
  
  // 2. metadata.coa_compliance (some transforms place it here)
  if (metadata.coa_compliance && typeof metadata.coa_compliance === 'object') {
    return metadata.coa_compliance as COACompliance;
  }
  
  // 3. metadata.compliance (MyMenu transform shape)
  if (metadata.compliance && typeof metadata.compliance === 'object') {
    return metadata.compliance as COACompliance;
  }
  
  // 4. Original nested path (direct DB queries)
  const coaData = metadata.coa_data as Record<string, unknown> | undefined;
  if (!coaData) return null;
  
  const compliance = coaData.coa_compliance as COACompliance | undefined;
  if (!compliance || typeof compliance !== 'object') return null;
  
  return compliance;
};

// Get all passing compliance badges from coa_compliance
// Returns an array of { key, label } for each passing test
export const getPassingComplianceBadges = (
  metadata: Record<string, unknown> | null | undefined,
  listing?: Record<string, unknown> | null,
): Array<{ key: string; label: string }> => {
  const compliance = getCOACompliance(metadata, listing);
  if (!compliance) return [];
  
  const badges: Array<{ key: string; label: string }> = [];
  
  Object.entries(compliance).forEach(([key, value]) => {
    if (value === true) {
      const label = COA_COMPLIANCE_LABELS[key] || formatCOAKey(key);
      badges.push({ key, label });
    }
  });
  
  return badges;
};

// Count of passing compliance tests
export const getPassingComplianceCount = (
  metadata: Record<string, unknown> | null | undefined,
  listing?: Record<string, unknown> | null,
): number => {
  const compliance = getCOACompliance(metadata, listing);
  if (!compliance) return 0;
  
  return Object.values(compliance).filter(v => v === true).length;
};

// Count passing compliance fields from coa_compliance object
// This is the primary way to check for passing tests - uses explicit coa_compliance data only
export const getCompliancePassCount = (compliance: COACompliance | null): number => {
  if (!compliance) return 0;
  return Object.values(compliance).filter(v => v === true).length;
};

// Get total terpenes from COA data - only uses explicit total_terpene field
export const getTotalTerpenes = (coaData: Record<string, Record<string, string | number>> | undefined): number | null => {
  if (!coaData?.terpenes) return null;
  
  const totalTerpene = coaData.terpenes.total_terpene;
  if (totalTerpene !== undefined && totalTerpene !== null) {
    if (typeof totalTerpene === 'number' && totalTerpene > 0) {
      return totalTerpene;
    }
    if (typeof totalTerpene === 'string') {
      const parsed = parseFloat(totalTerpene);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
  }
  
  return null;
};

// Format array values (certifications, accreditations) as comma-separated string
export const formatArrayValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  return String(value);
};

// Extract method(s) from category data
export const getCategoryMethod = (categoryData: Record<string, unknown>): string | null => {
  if (!categoryData) return null;
  
  const method = categoryData.method;
  const methods = categoryData.methods;
  
  if (typeof method === 'string') {
    return method;
  }
  
  if (Array.isArray(methods)) {
    return methods.join(', ');
  }
  
  return null;
};

// Check if a field is client info (should be hidden)
export const isClientInfoField = (fieldName: string): boolean => {
  const lowerField = fieldName.toLowerCase();
  return CLIENT_INFO_FIELDS.some(clientField => 
    lowerField === clientField || 
    lowerField.includes('client') ||
    lowerField.includes('submitter') ||
    lowerField.includes('customer') ||
    lowerField.includes('licensee')
  );
};

// Category metadata fields (non-test-result fields) that should appear at top of sections
export const CATEGORY_METADATA_FIELDS = [
  'method',
  'methods',
  'sample_date',
  'test_date',
  'received_date',
  'report_date',
  'analysis_date',
  'sample_id',
  'batch_id',
  'lot_number',
  'lot_id',
  'batch_number',
  'unit',
  'units',
  'status',
  'result',
  'overall_result',
  'pass_fail',
  'dilution',
  'dilution_factor',
] as const;

// Check if a field is category metadata (not a test result)
export const isCategoryMetadataField = (fieldName: string): boolean => {
  const lowerField = fieldName.toLowerCase();
  return CATEGORY_METADATA_FIELDS.some(metaField => 
    lowerField === metaField || 
    lowerField.includes('_date') ||
    lowerField.includes('_id') && !lowerField.includes('acid') // avoid matching "thca_id" etc
  );
};

// Get category data without method fields and client info
export const getCategoryDataWithoutMethod = (categoryData: Record<string, unknown>): Record<string, string | number> => {
  const result: Record<string, string | number> = {};
  
  Object.entries(categoryData).forEach(([key, value]) => {
    // Skip method fields and client info fields
    if (key !== 'method' && key !== 'methods' && !isClientInfoField(key)) {
      if (typeof value === 'string' || typeof value === 'number') {
        result[key] = value;
      }
    }
  });
  
  return result;
};

// Get category metadata fields (dates, IDs, status - excluding method which is handled separately)
export const getCategoryMetadata = (categoryData: Record<string, unknown>): Record<string, string | number> => {
  const result: Record<string, string | number> = {};
  
  Object.entries(categoryData).forEach(([key, value]) => {
    // Skip method fields (handled separately) and client info
    if (key === 'method' || key === 'methods' || isClientInfoField(key)) return;
    
    if (isCategoryMetadataField(key) && (typeof value === 'string' || typeof value === 'number')) {
      result[key] = value;
    }
  });
  
  return result;
};

// Get only test result fields (excludes metadata and method)
export const getCategoryTestResults = (categoryData: Record<string, unknown>): Record<string, string | number> => {
  const result: Record<string, string | number> = {};
  
  Object.entries(categoryData).forEach(([key, value]) => {
    // Skip method fields, client info, and metadata fields
    if (key === 'method' || key === 'methods' || isClientInfoField(key) || isCategoryMetadataField(key)) return;
    
    if (typeof value === 'string' || typeof value === 'number') {
      result[key] = value;
    }
  });
  
  return result;
};

// Filter out client info from lab_info object
export const filterLabInfo = (labInfo: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  
  Object.entries(labInfo).forEach(([key, value]) => {
    if (!isClientInfoField(key)) {
      result[key] = value;
    }
  });
  
  return result;
};

// Get terpene percentage for listing display
// Priority: listings.terpene_percent > coa_data.terpenes.total_terpene > metadata.total_terpene
export const getListingTerpenePercent = (
  terpenePercent: number | null | undefined,
  metadata: Record<string, unknown> | null | undefined
): number | null => {
  // Priority 1: Direct terpene_percent column from listings table
  if (typeof terpenePercent === 'number' && terpenePercent > 0) {
    return terpenePercent;
  }
  
  if (!metadata) return null;
  
  // Priority 2: COA data total_terpene field (merged from both reports)
  const coaData = mergeCOAData(metadata);
  const coaTerpenes = getTotalTerpenes(coaData);
  if (coaTerpenes !== null) return coaTerpenes;
  
  // Priority 3: Direct metadata total_terpene field
  if (typeof metadata.total_terpene === 'number') {
    return metadata.total_terpene;
  }
  if (typeof metadata.total_terpene === 'string') {
    const val = parseFloat(metadata.total_terpene);
    if (!isNaN(val) && val > 0) return val;
  }
  
  // Legacy fallback: older field names
  if (typeof metadata.terpenes_percent_total === 'number') {
    return metadata.terpenes_percent_total;
  }
  if (typeof metadata.terp_percent === 'number') {
    return metadata.terp_percent;
  }
  if (typeof metadata.terpenes_percent_total === 'string') {
    const val = parseFloat(metadata.terpenes_percent_total);
    if (!isNaN(val)) return val;
  }
  if (typeof metadata.terp_percent === 'string') {
    const val = parseFloat(metadata.terp_percent);
    if (!isNaN(val)) return val;
  }
  
  return null;
};
